import React, { useContext, useEffect, useRef, useState } from "react";
import { ReactComponent as CloseIcon } from "../../../../assets/modal-close.svg";
import { useContentAndProgress } from "../../../../contexts/UserProgressContext";
import { ReactComponent as ArrowDown } from "../../../../assets/arrow-down.svg";
import { ReactComponent as ArrowUp } from "../../../../assets/arrow-up.svg";
import ArrowRight from "../../../../assets/arrows-right.svg";
import { MilestonesList } from "../milestones/milestonesList";
import { useTranslation } from "react-i18next";
import { IContentWithProgress } from "../../../../@types";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../contexts/AuthContext";
import UnlockPhaseModal from "../content/components/UnlockPhaseModal";

interface PhaseListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PhaseListModal: React.FC<PhaseListModalProps> = ({ isOpen, onClose }) => {
  const { journey, phases, milestones, activePhase } = useContentAndProgress();
  const { user } = useContext(AuthContext);

  const journeyId = user?.userDetails.journeyId;
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const { t: tunlock } = useTranslation("unlock");

  const activePhaseIndex = journey?.content?.childContentIds.findIndex(
    (childContentId) =>
      phases?.some(
        (phase) =>
          phase.content.platformContentId === childContentId &&
          phase === activePhase
      )
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const [expandedPhaseIndex, setExpandedPhaseIndex] = useState<number | null>(
    null
  );
  const phaseRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (activePhaseIndex !== null && activePhaseIndex !== undefined) {
      setExpandedPhaseIndex(activePhaseIndex);
    }
  }, [activePhaseIndex]);

  useEffect(() => {
    if (expandedPhaseIndex !== null && phaseRefs.current[expandedPhaseIndex]) {
      phaseRefs.current[expandedPhaseIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [expandedPhaseIndex]);

  const handleExpand = (
    index: number,
    matchingPhase?: IContentWithProgress
  ) => {
    const firstMilestone = milestones
      ?.filter((ms: IContentWithProgress) =>
        matchingPhase?.content.childContentIds.includes(
          ms.content.platformContentId
        )
      )
      .sort(
        (ms1: IContentWithProgress, ms2: IContentWithProgress) =>
          ms1.content.orderForProgress[journeyId ?? ""] -
          ms2.content.orderForProgress[journeyId ?? ""]
      )[0];

    navigate(
      `/phase/${matchingPhase?.content.platformContentId}/milestone/${firstMilestone?.content.platformContentId}`
    );

    setExpandedPhaseIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleGoToContent = (
    phaseId: string,
    milestoneId: string,
    firstContentId?: string // Content ID is optional
  ) => {
    if (firstContentId) {
      navigate(
        `/phase/${phaseId}/milestone/${milestoneId}/content/${firstContentId}`
      );
    } else {
      // Fallback: Go to the milestone page if no content exists (or is defined)
      navigate(`/phase/${phaseId}/milestone/${milestoneId}`);
    }
    onClose(); // Close modal on navigation
  };

  if (!isOpen) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="w-full h-full bg-[#FFDA6F] relative overflow-y-auto">
        {/* X button to close the modal */}
        <div
          className="flex justify-end items-center gap-2"
          style={{ height: "64px", padding: "18px" }}
        >
          <button
            className="cursor-pointer"
            onClick={onClose} // Close the modal
          >
            <CloseIcon />
          </button>
        </div>
        {/* Render a div for each phase in the journey */}
        <div className="flex flex-wrap justify-between items-start gap-3 px-6">
          {journey?.content?.childContentIds.map((childContentId, index) => {
            const matchingPhase = phases?.find(
              (phase) => phase.content.platformContentId === childContentId
            );

            const lastPhase = phases?.[phases.length - 1];

            const isLastPhase = lastPhase === matchingPhase;

            const hasJobType = user?.userDetails?.newJobType;
            const isRestartPhase = lastPhase?.content?.restartPhase;

            return (
              <div
                key={childContentId}
                ref={(el) => (phaseRefs.current[index] = el)}
                className="bg-white p-6 rounded-lg mb-2 min-w-full"
              >
                <div className="flex justify-between items-center mb-[12px]">
                  <div className="flex flex-col">
                    <h2 className="font-[300] text-[14px] leading-4">{`Phase ${
                      index + 1
                    }`}</h2>
                    <h2 className="font-[700] text-[24px] leading-7">
                      {matchingPhase?.title}
                    </h2>
                  </div>
                  <div
                    className="progress-bar text-xs font-medium text-black lg:hidden ml-auto "
                    style={{
                      background: `radial-gradient(closest-side, white 80%, transparent 82% 100%), conic-gradient(#FDC300 ${Math.round(
                        matchingPhase?.progressValue || 0
                      )}%, black 0)`,
                    }}
                  >
                    {Math.round(matchingPhase?.progressValue || 0)}%
                  </div>
                </div>
                <hr className="border-xs border-[#A7B6C5] w-full mb-[12px]" />

                <div className="mt-4 font-[500] text-[14px] leading-4 mb-[12px]">
                  {matchingPhase?.description}
                </div>
                {isLastPhase && !hasJobType && isRestartPhase ? (
                  <div
                    className="bg-Blue-ligth rounded-lg cursor-pointer p-3"
                    onClick={handleOpenModal}
                  >
                    <div className=" text-[#757575]">
                      {tunlock("mobileListText")}
                    </div>
                    <div className=" text-[#757575] flex justify-end font-bold">
                      {tunlock("mobileListButton1")}{" "}
                      {(lastPhase?.content?.orderForProgress[journeyId ?? ""] ||
                        0) + 1}{" "}
                      {tunlock("mobileListButton2")}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end mr-2">
                    <div
                      className="cursor-pointer flex items-center"
                      onClick={() => handleExpand(index, matchingPhase)}
                    >
                      <span
                        className={`mr-4 text-[#757575] font-[600] text-[14px] leading-4 ${
                          expandedPhaseIndex === index ? "mb-4" : ""
                        }`}
                      >
                        {t("expand-milestones")}
                      </span>
                      {expandedPhaseIndex === index ? (
                        <ArrowUp
                          className={`mt-[1%] ${
                            expandedPhaseIndex === index ? "mb-4" : ""
                          }`}
                        />
                      ) : (
                        <ArrowDown className="mt-[1%]" />
                      )}
                    </div>
                  </div>
                )}

                {expandedPhaseIndex === index && (
                  <div className="w-full">
                    <MilestonesList isMobile={onClose} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <UnlockPhaseModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          activePhase={activePhase}
        />
      </div>
    </div>
  );
};

export default PhaseListModal;
